// Konstruktions-Checkliste — laufend aktualisierte Warnungen, die teure
// Werkstattfehler verhindern. Engine-frei, reine Funktionen: jede Regel ist
// einzeln aufrufbar und einzeln testbar, `runChecks` fasst sie zusammen.
//
// Ein Fund ist immer { severity, code, message, clientIds }:
//   severity  "error"   — so gebaut nicht herstellbar/haltbar
//             "warning" — geht schief oder hält nicht lange
//             "hint"    — stört nur die Weiterverarbeitung (Stückliste)
//   clientIds — betroffene Teile, das erste ist das Sprungziel der UI.
//
// Bewusst genau sechs Regeln: lieber wenige, die stimmen, als viele, die
// Rauschen erzeugen. Geometrie kommt durchweg aus den vorhandenen Helfern
// (snapping/collision/obstacles/…), hier wird nichts nachgebaut.

import { partAabb, rotateLocalVector } from "./snapping"
import { isHidden } from "./visibility"
import { collidingPairs, CONTACT_TOLERANCE_MM } from "./collision"
import { anchorOf } from "./relations"
import { cutoutsOf, isCircle, cutoutAxis } from "./cutouts"
import { groovesOf } from "./machinings"
import { cutoutBox, grooveBox, WEB_MM } from "./obstacles"
import { hardwareOf } from "./hardware"
import { assessShelf } from "./deflection"

export const SEVERITY_ORDER = [ "error", "warning", "hint" ]

// --- gemeinsame Grenzwerte ---------------------------------------------------

// Unterkante bis 1 mm über dem Boden gilt als aufliegend (Rundungsreste aus
// Drehungen, Ganzzahl-mm)
export const GROUND_TOLERANCE_MM = 1

// Beschlag-Mindeststärken. Begründung steckt in der jeweiligen Zeile —
// maßgeblich ist immer der Restboden unter dem Topf: bleibt zu wenig Holz
// stehen, drückt sich der Beschlag beim Anziehen durch die Sichtfläche.
export const HARDWARE_BORES = [
  {
    key: "topfband",
    label: "Topfband Ø35",
    diameterMm: 35,
    // Topf T13 (Standard-Topfband) + ~2 mm Restboden ⇒ 15 mm. Unter 15 mm
    // drückt der Topf durch bzw. reißt beim Anziehen der Bandschrauben aus.
    minThicknessMm: 15
  },
  {
    key: "exzenter",
    label: "Exzenter Ø15",
    diameterMm: 15,
    // Topf T12,5 + ~3,5 mm Restboden ⇒ 16 mm. Der Exzenter zieht auf Zug, der
    // Restboden trägt die ganze Verbindungskraft — deshalb mehr Reserve als
    // beim Topfband.
    minThicknessMm: 16
  }
]

// Ein Auflager muss mindestens die Hälfte der Bodentiefe untergreifen. Damit
// zählt eine Seitenwand (volle Tiefe) als Auflager, eine 19 mm schmale
// Rückwand dagegen nicht — sie hält den hinteren Rand, den Durchhang vorn
// verhindert sie nicht.
export const SUPPORT_COVERAGE = 0.5

// Ab |cos| > 0,95 gilt die Stärkenachse als senkrecht — bis ~18° Schrägstellung
// ist ein Boden noch ein Boden.
const HORIZONTAL_COSINE = 0.95

const finding = (severity, code, message, clientIds) => ({ severity, code, message, clientIds })

// deutsche Zahl mit einer Nachkommastelle
const mm = (value) => value.toFixed(1).replace(".", ",")

const visibleParts = (parts) => parts.filter((part) => !isHidden(part))

const nameOf = (part) => part?.name ?? "Teil"

// --- Regel 1: schwebendes Teil ----------------------------------------------

// Flächenkontakt zweier Welt-AABBs: auf keiner Achse getrennt und auf
// mindestens zwei Achsen echt überdeckend (Kanten-/Eckberührung zählt nicht).
export function aabbsInContact(a, b, tolerance = CONTACT_TOLERANCE_MM) {
  let overlapping = 0
  for (const axis of [ 0, 1, 2 ]) {
    const gap = Math.max(b.min[axis] - a.max[axis], a.min[axis] - b.max[axis])
    if (gap > tolerance) return false
    if (gap < -tolerance) overlapping++
  }
  return overlapping >= 2
}

// Teile ohne Auflager, ohne Kontakt und ohne Heft-Beziehung — der vergessene
// Fachboden, der im Modell in der Luft hängt.
export function floatingParts(parts, tolerance = CONTACT_TOLERANCE_MM) {
  const visible = visibleParts(parts)
  const boxes = new Map(visible.map((part) => [ part.client_id, partAabb(part) ]))
  const anchored = new Set()
  for (const part of visible) {
    const anchor = anchorOf(part)
    if (!anchor) continue
    // geheftet zählt in beide Richtungen: Anker wie Anhänger sind verbunden
    anchored.add(part.client_id)
    anchored.add(anchor)
  }

  return visible
    .filter((part) => {
      if (anchored.has(part.client_id)) return false
      const box = boxes.get(part.client_id)
      if (box.min[1] <= GROUND_TOLERANCE_MM) return false // steht auf dem Boden
      return !visible.some((other) =>
        other.client_id !== part.client_id &&
        aabbsInContact(box, boxes.get(other.client_id), tolerance))
    })
    .map((part) => finding(
      "warning", "floating",
      `„${nameOf(part)}“ schwebt: kein Auflager, kein Kontakt zu einem anderen Teil.`,
      [ part.client_id ]
    ))
}

// --- Regel 2: zu dünn für den Beschlag --------------------------------------

// Welche Beschlag-Bohrbilder stecken im Teil? Erkannt wird die Geometrie
// (Flächenbohrung mit dem typischen Durchmesser); die Beschlagliste dient als
// Rückfall, falls das Bohrbild (noch) nicht darstellbar war.
function hardwareKindsOf(part) {
  const kinds = new Set()
  for (const cutout of cutoutsOf(part)) {
    if (!isCircle(cutout) || cutoutAxis(cutout) !== "y") continue
    const preset = HARDWARE_BORES.find((entry) => Math.abs(Number(cutout.diameter_mm) - entry.diameterMm) < 0.5)
    if (preset) kinds.add(preset.key)
  }
  for (const entry of hardwareOf(part)) {
    if (HARDWARE_BORES.some((preset) => preset.key === entry.type)) kinds.add(entry.type)
  }
  return kinds
}

export function thinForHardware(parts) {
  const findings = []
  for (const part of visibleParts(parts)) {
    const kinds = hardwareKindsOf(part)
    for (const preset of HARDWARE_BORES) {
      if (!kinds.has(preset.key)) continue
      if (part.thickness_mm >= preset.minThicknessMm) continue
      findings.push(finding(
        "error", "hardware_thickness",
        `„${nameOf(part)}“ ist mit ${part.thickness_mm} mm zu dünn für ${preset.label} ` +
        `(mindestens ${preset.minThicknessMm} mm).`,
        [ part.client_id ]
      ))
    }
  }
  return findings
}

// --- Regel 3: Bearbeitung zu nah an der Kante -------------------------------

// Hüllboxen aller Bearbeitungen eines Teils samt Eintrittsachse. Die Boxen
// selbst kommen aus obstacles.js — hier wird nur die Achse ergänzt, denn in
// Eintrittsrichtung ist ein Abtrag ja gewollt offen.
function machiningBoxes(part) {
  return [
    ...cutoutsOf(part).map((cutout) => ({ box: cutoutBox(part, cutout), entryAxis: cutoutAxis(cutout) })),
    // Nut und Falz werden immer von einer Fläche aus gearbeitet ⇒ Achse y
    ...groovesOf(part).map((groove) => ({ box: grooveBox(part, groove), entryAxis: "y" }))
  ]
}

// Kleinster Reststeg einer Hüllbox zu den Teilkanten quer zur Eintrittsachse.
// Seiten, an denen die Box die Teilfläche erreicht (Durchbruch, auslaufende
// Nut), bleiben außen vor — dort ist kein Steg geplant.
export function smallestWebMm(part, { box, entryAxis }) {
  const half = [ part.length_mm / 2, part.thickness_mm / 2, part.width_mm / 2 ]
  const entry = { x: 0, y: 1, z: 2 }[entryAxis]
  let smallest = Infinity
  for (const axis of [ 0, 1, 2 ]) {
    if (axis === entry) continue
    for (const web of [ box.min[axis] + half[axis], half[axis] - box.max[axis] ]) {
      if (web > 1e-6) smallest = Math.min(smallest, web)
    }
  }
  return Number.isFinite(smallest) ? smallest : null
}

// Ein Fund je Teil (nicht je Bohrung) — sonst überschwemmt eine Lochreihe die
// Liste. Gemeldet wird der dünnste Steg.
export function machiningsNearEdge(parts, minWebMm = WEB_MM) {
  const findings = []
  for (const part of visibleParts(parts)) {
    let count = 0
    let worst = Infinity
    for (const machining of machiningBoxes(part)) {
      const web = smallestWebMm(part, machining)
      if (web === null || web >= minWebMm) continue
      count++
      worst = Math.min(worst, web)
    }
    if (count === 0) continue
    findings.push(finding(
      "warning", "edge_distance",
      `„${nameOf(part)}“: ${count === 1 ? "eine Bearbeitung liegt" : `${count} Bearbeitungen liegen`} ` +
      `zu nah an der Kante (${mm(worst)} mm Restholz, mindestens ${minWebMm} mm).`,
      [ part.client_id ]
    ))
  }
  return findings
}

// --- Regel 4: Fachboden-Durchbiegung ----------------------------------------

// Liegt die Stärke des Teils senkrecht, ist es ein liegendes Brett.
export function isHorizontalPlate(part) {
  return Math.abs(rotateLocalVector(part, [ 0, 1, 0 ])[1]) > HORIZONTAL_COSINE
}

// Freie Spannweite eines liegenden Bretts: größter Abstand zwischen zwei
// tragenden Nachbarteilen entlang derselben waagerechten Achse. Ein Auflager
// muss von unten kommen (reicht bis zur Unterseite hoch und beginnt darunter)
// und mindestens SUPPORT_COVERAGE der Bodentiefe untergreifen.
// Liefert { spanMm, depthMm, axis } oder null, wenn es keine zwei Auflager gibt.
export function freeSpanOf(part, parts, tolerance = CONTACT_TOLERANCE_MM) {
  if (!isHorizontalPlate(part)) return null
  const shelf = partAabb(part)
  const others = visibleParts(parts).filter((other) => other.client_id !== part.client_id)

  let best = null
  for (const axis of [ 0, 2 ]) {
    const cross = axis === 0 ? 2 : 0
    const depthMm = shelf.max[cross] - shelf.min[cross]
    if (depthMm <= 0) continue

    const spans = []
    for (const other of others) {
      const box = partAabb(other)
      // trägt von unten?
      if (box.max[1] < shelf.min[1] - tolerance) continue
      if (box.min[1] > shelf.min[1] + tolerance) continue
      // untergreift genug Tiefe?
      const coverage = Math.min(box.max[cross], shelf.max[cross]) - Math.max(box.min[cross], shelf.min[cross])
      if (coverage < depthMm * SUPPORT_COVERAGE) continue
      // liegt entlang der Spannachse am/unter dem Boden?
      if (box.max[axis] < shelf.min[axis] - tolerance) continue
      if (box.min[axis] > shelf.max[axis] + tolerance) continue
      spans.push([
        Math.max(box.min[axis], shelf.min[axis]),
        Math.min(box.max[axis], shelf.max[axis])
      ])
    }
    if (spans.length < 2) continue

    spans.sort((a, b) => a[0] - b[0])
    let reach = spans[0][1]
    let gap = 0
    for (const [ start, end ] of spans.slice(1)) {
      gap = Math.max(gap, start - reach)
      reach = Math.max(reach, end)
    }
    if (gap <= 0) continue
    // Mehrere Achsen mit Auflagern: die kürzere Spannweite trägt die Last
    if (!best || gap < best.spanMm) best = { spanMm: gap, depthMm, axis }
  }
  return best
}

// Durchbiegung EINES Teils im Zusammenhang des Modells — dieselbe Erkennung,
// die auch die Checkliste benutzt, aber ohne Fund-Text. Damit kann das
// Details-Panel den Wert anzeigen, ohne die Auflager-Logik nachzubauen.
// Liefert die Beurteilung aus deflection.js (inkl. spanMm) oder null, wenn das
// Teil kein waagerechter Boden mit zwei Auflagern ist.
export function shelfAssessment(part, parts, materialName = null) {
  const span = freeSpanOf(part, parts)
  if (!span) return null
  return assessShelf({
    spanMm: span.spanMm,
    thicknessMm: part.thickness_mm,
    widthMm: span.depthMm,
    materialName
  })
}

// materialNameOf: (part) => Materialname (für den E-Modul); fehlt er, rechnet
// deflection.js konservativ mit Spanplatte.
export function saggingShelves(parts, materialNameOf = () => null) {
  const findings = []
  for (const part of visibleParts(parts)) {
    const result = shelfAssessment(part, parts, materialNameOf(part))
    if (!result?.critical) continue
    findings.push(finding(
      "warning", "shelf_deflection",
      `„${nameOf(part)}“ biegt sich bei ${Math.round(result.spanMm)} mm freier Spannweite um ` +
      `ca. ${mm(result.deflectionMm)} mm durch (Grenze ${mm(result.limitMm)} mm). ` +
      "Dicker, kürzer oder mit Mittelstütze bauen.",
      [ part.client_id ]
    ))
  }
  return findings
}

// --- Regel 5: Teil ohne Material --------------------------------------------

// Ausgeblendete Teile zählen mit: sie stehen weiter in Stückliste und
// Zuschnittplan (vgl. visibility.js).
export function partsWithoutMaterial(parts) {
  const without = parts.filter((part) => part.material_id === null || part.material_id === undefined)
  if (without.length === 0) return []
  const names = without.slice(0, 3).map((part) => `„${nameOf(part)}“`).join(", ")
  const rest = without.length > 3 ? ` und ${without.length - 3} weitere` : ""
  return [ finding(
    "hint", "missing_material",
    `${without.length === 1 ? "Ein Teil" : `${without.length} Teile`} ohne Material (${names}${rest}) — ` +
    "Stückliste und Kalkulation bleiben unvollständig.",
    without.map((part) => part.client_id)
  ) ]
}

// --- Regel 6: Kollisionen ----------------------------------------------------

export function collisions(parts) {
  const byId = new Map(parts.map((part) => [ part.client_id, part ]))
  return collidingPairs(parts).map(([ a, b ]) => finding(
    "error", "collision",
    `„${nameOf(byId.get(a))}“ und „${nameOf(byId.get(b))}“ durchdringen sich.`,
    [ a, b ]
  ))
}

// --- Alles zusammen ----------------------------------------------------------

// options.materials: [{ id, name }] — nur für den E-Modul der Durchbiegung.
export function runChecks(parts = [], options = {}) {
  if (parts.length === 0) return []
  const materials = new Map((options.materials ?? []).map((material) => [ material.id, material ]))
  const materialNameOf = (part) => materials.get(part.material_id)?.name ?? null

  const findings = [
    ...collisions(parts),
    ...thinForHardware(parts),
    ...floatingParts(parts),
    ...machiningsNearEdge(parts),
    ...saggingShelves(parts, materialNameOf),
    ...partsWithoutMaterial(parts)
  ]
  // stabile Reihenfolge: erst Schwere, dann die Reihenfolge der Regeln oben
  return findings
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) =>
      SEVERITY_ORDER.indexOf(a.entry.severity) - SEVERITY_ORDER.indexOf(b.entry.severity) ||
      a.index - b.index)
    .map(({ entry }) => entry)
}

// Anzahl je Schweregrad — für Badge und Panel-Kopf.
export function countsBySeverity(findings) {
  const counts = { error: 0, warning: 0, hint: 0 }
  for (const entry of findings) counts[entry.severity]++
  return counts
}
